import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, useToast } from '@w7/ui'
import { useState, type FormEvent } from 'react'
import { useCmMe } from '@/api/hooks/useCmMe'
import { cmApi, type SecurityIpWhitelistRow } from '@/api/cmApi'
import { canAddCmIpWhitelist, canDeleteCmIpWhitelist } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { enabledChip } from './securityUi'
import { SecurityCrudPage } from './SecurityCrudPage'

/** CM operator allowlist (existing cm.ip_whitelist + API guard). */
export function IpWhitelistPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const canAdd = canAddCmIpWhitelist(me.data?.caller)
  const canDelete = canDeleteCmIpWhitelist(me.data?.caller)
  const [ip, setIp] = useState('')
  const [label, setLabel] = useState('')

  const saveM = useMutation({
    mutationFn: () =>
      cmApi.securityIpWhitelistCreate(authHeaders(), {
        ip: ip.trim(),
        label: label.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success('Whitelist IP saved')
      setIp('')
      setLabel('')
      await qc.invalidateQueries({ queryKey: ['cm', 'security', 'ip-whitelist'] })
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  return (
    <SecurityCrudPage<SecurityIpWhitelistRow>
      title="CM IP whitelist"
      subtitle="Operator IPs allowed to call CM-API"
      queryKey="ip-whitelist"
      emptyHint="No whitelist rows. Guard denies all until at least one IP is enabled."
      listFn={cmApi.securityIpWhitelistList}
      deleteFn={canDelete ? cmApi.securityIpWhitelistDelete : undefined}
      deleteConfirm="Delete this CM allowlist IP? The last enabled IP cannot be removed (API blocks lockout)."
      columns={[
        { key: 'ip', header: 'IP', cell: (r) => r.ip },
        { key: 'label', header: 'Label', cell: (r) => r.label ?? '—' },
        { key: 'enabled', header: 'Enabled', cell: (r) => enabledChip(r.enabled) },
        {
          key: 'when',
          header: 'Created',
          cell: (r) => new Date(r.createdAt).toLocaleString(),
        },
      ]}
      form={
        canAdd ? (
          <form
            className="cm-form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault()
              saveM.mutate()
            }}
          >
            <div className="cm-form-grid">
              <Field label="Host IP" required>
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    required
                    value={ip}
                    onChange={(e) => setIp(e.target.value)}
                    placeholder="e.g. 1.2.3.4"
                  />
                )}
              </Field>
              <Field label="Label">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Optional"
                  />
                )}
              </Field>
              <div className="cm-form-actions">
                <Button type="submit" busy={saveM.isPending}>
                  Add / update
                </Button>
              </div>
            </div>
          </form>
        ) : undefined
      }
    />
  )
}
